import axios from 'axios';
import { API_BASE_URL } from '../config';
import { getAuthHeadersFromStorage } from '../utils/authHeaders';
import { attachAuthInterceptor } from '../utils/attachAuthInterceptor';

const client = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

client.interceptors.request.use((config) => {
  const authHeaders = getAuthHeadersFromStorage();
  if (Object.keys(authHeaders).length) {
    config.headers = { ...authHeaders, ...config.headers };
  }
  return config;
});
attachAuthInterceptor(client);

const extractError = (error) => {
  if (error?.response?.data?.detail) {
    return Array.isArray(error.response.data.detail)
      ? error.response.data.detail.join(', ')
      : error.response.data.detail;
  }
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  return error.message || 'Request failed';
};

export const fetchMaskingSettings = async () => {
  try {
    const { data } = await client.get('/api/v1/ai-image-masking/settings');
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const updateMaskingSettings = async (payload) => {
  try {
    const { data } = await client.put('/api/v1/ai-image-masking/settings', payload);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const listSwatches = async (params = {}) => {
  try {
    const { data } = await client.get('/api/v1/ai-image-masking/swatches', { params });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const createSwatch = async ({ file, name, description }) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', name);
    formData.append('description', description);

    const { data } = await client.post('/api/v1/ai-image-masking/swatches', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const listSwatchesV2 = async (params = {}) => {
  try {
    const { data } = await client.get('/api/v1/ai-image-masking/swatches/v2', { params });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const createSwatchV2 = async ({ file, name, description, tags }) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', name);
    formData.append('description', description);
    if (tags && Array.isArray(tags) && tags.length > 0) {
      tags.forEach((tag) => formData.append('tags', tag));
    }

    const { data } = await client.post('/api/v1/ai-image-masking/swatches/v2', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const deleteSwatchV2 = async (swatchId) => {
  try {
    await client.delete(`/api/v1/ai-image-masking/swatches/${swatchId}`);
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const deleteSwatchesV2 = async (ids = []) => {
  try {
    await client.post('/api/v1/ai-image-masking/swatches/bulk-delete', { ids });
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const updateSwatchV2 = async (swatchId, payload) => {
  try {
    const { data } = await client.put(`/api/v1/ai-image-masking/swatches/v2/${swatchId}`, payload);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const updateSwatchImageV2 = async (swatchId, file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await client.put(`/api/v1/ai-image-masking/swatches/v2/${swatchId}/image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const deleteSwatch = async (swatchId) => {
  try {
    const { data } = await client.delete(`/api/v1/ai-image-masking/swatches/${swatchId}`);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const processTryOn = async ({ environmentFile, swatchId, prompt, userId, agentTag, forceOverwrite = false }) => {
  try {
    const formData = new FormData();
    formData.append('environment_image', environmentFile);
    formData.append('swatch_id', swatchId);
    if (userId) {
      formData.append('user_id', userId);
    }
    if (prompt) {
      formData.append('prompt', prompt);
    }
    if (agentTag) {
      formData.append('agent_tag', agentTag);
    }
    formData.append('force_overwrite', forceOverwrite ? 'true' : 'false');

    const { data } = await client.post('/api/v1/ai-image-masking/try-on', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  } catch (error) {
    if (error?.response?.status === 409 && error?.response?.data?.detail?.code === 'tryon_exists') {
      const err = new Error(error.response.data.detail.message || 'Existing try-on found');
      err.code = error.response.data.detail.code;
      err.payload = error.response.data.detail;
      throw err;
    }
    throw new Error(extractError(error));
  }
};

export const createTryOnJob = async ({
  environmentFile,
  swatchId,
  prompt,
  userId,
  agentTag,
  forceOverwrite = false,
  additionalInstructions,
}) => {
  try {
    const formData = new FormData();
    formData.append('environment_image', environmentFile);
    formData.append('swatch_id', swatchId);
    formData.append('user_id', userId);
    if (prompt) {
      formData.append('prompt', prompt);
    }
    if (agentTag) {
      formData.append('agent_tag', agentTag);
    }
    formData.append('force_overwrite', forceOverwrite ? 'true' : 'false');
    if (additionalInstructions) {
      formData.append('additional_instructions', additionalInstructions);
    }

    const { data } = await client.post('/api/v1/ai-image-masking/try-on/jobs', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  } catch (error) {
    if (error?.response?.status === 409 && error?.response?.data?.detail?.code === 'tryon_exists') {
      const err = new Error(error.response.data.detail.message || 'Existing try-on found');
      err.code = error.response.data.detail.code;
      err.payload = error.response.data.detail;
      throw err;
    }
    throw new Error(extractError(error));
  }
};

export const createTryOnJobFromGallery = async ({
  galleryId,
  swatchId,
  userId,
  prompt,
  agentTag,
  forceOverwrite = false,
  additionalInstructions,
}) => {
  try {
    const formData = new FormData();
    formData.append('gallery_id', galleryId);
    formData.append('swatch_id', swatchId);
    formData.append('user_id', userId);
    if (prompt) {
      formData.append('prompt', prompt);
    }
    if (agentTag) {
      formData.append('agent_tag', agentTag);
    }
    formData.append('force_overwrite', forceOverwrite ? 'true' : 'false');
    if (additionalInstructions) {
      formData.append('additional_instructions', additionalInstructions);
    }

    const { data } = await client.post('/api/v1/ai-image-masking/try-on/jobs/from-gallery', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  } catch (error) {
    if (error?.response?.status === 409 && error?.response?.data?.detail?.code === 'tryon_exists') {
      const err = new Error(error.response.data.detail.message || 'Existing try-on found');
      err.code = error.response.data.detail.code;
      err.payload = error.response.data.detail;
      throw err;
    }
    throw new Error(extractError(error));
  }
};

export const createBatchTryOnJob = async ({
  environmentFile,
  swatchIds,
  prompt,
  userId,
  agentTag,
  additionalInstructions,
}) => {
  try {
    const formData = new FormData();
    formData.append('environment_image', environmentFile);
    (swatchIds || []).forEach((id) => formData.append('swatch_ids', id));
    formData.append('user_id', userId);
    if (prompt) {
      formData.append('prompt', prompt);
    }
    if (agentTag) {
      formData.append('agent_tag', agentTag);
    }
    if (additionalInstructions) {
      formData.append('additional_instructions', additionalInstructions);
    }

    const { data } = await client.post('/api/v1/ai-image-masking/try-on/batch-jobs', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const getTryOnJobStatus = async (jobId) => {
  try {
    const { data } = await client.get(`/api/v1/ai-image-masking/try-on/jobs/${jobId}`);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const listTryOnJobs = async (params = {}) => {
  try {
    const { data } = await client.get('/api/v1/ai-image-masking/try-on/jobs', { params });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const getBatchTryOnJobStatus = async (jobId) => {
  try {
    const { data } = await client.get(`/api/v1/ai-image-masking/try-on/batch-jobs/${jobId}`);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const listBatchTryOnJobs = async (params = {}) => {
  try {
    const { data } = await client.get('/api/v1/ai-image-masking/try-on/batch-jobs', { params });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const listGallery = async (params = {}) => {
  try {
    const { data } = await client.get('/api/v1/ai-image-masking/gallery', { params });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const deleteGalleryEntry = async (galleryId) => {
  try {
    const { data } = await client.delete(`/api/v1/ai-image-masking/gallery/${galleryId}`);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const deleteGalleryForUser = async (userId) => {
  try {
    const { data } = await client.delete(`/api/v1/ai-image-masking/gallery/users/${encodeURIComponent(userId)}`);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const listGalleryUsers = async (params = {}) => {
  try {
    const { data } = await client.get('/api/v1/ai-image-masking/gallery/users', { params });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const listAgents = async () => {
  try {
    const { data } = await client.get('/api/v1/ai-image-masking/agents');
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const createAgent = async (payload) => {
  try {
    const { data } = await client.post('/api/v1/ai-image-masking/agents', payload);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const updateAgent = async (agentId, payload) => {
  try {
    const { data } = await client.put(`/api/v1/ai-image-masking/agents/${agentId}`, payload);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const deleteAgent = async (agentId) => {
  try {
    await client.delete(`/api/v1/ai-image-masking/agents/${agentId}`);
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const listAgentHistory = async (agentId) => {
  try {
    const { data } = await client.get(`/api/v1/ai-image-masking/agents/${agentId}/history`);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const fetchSettingsHistory = async (params = {}) => {
  try {
    const { data } = await client.get('/api/v1/ai-image-masking/settings/history', { params });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const restoreSettingsVersion = async (historyId) => {
  try {
    const { data } = await client.post(`/api/v1/ai-image-masking/settings/history/${historyId}/restore`);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const createZipImportJob = async (file, duplicateStrategy = 'overwrite') => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('duplicate_strategy', duplicateStrategy);
    const { data } = await client.post('/api/v1/ai-image-masking/import-zip', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const getZipImportJob = async (jobId) => {
  try {
    const { data } = await client.get(`/api/v1/ai-image-masking/import-jobs/${jobId}`);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const listZipImportJobs = async (params = {}) => {
  try {
    const { data } = await client.get('/api/v1/ai-image-masking/import-jobs', { params });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const cancelZipImportJob = async (jobId, { cleanup = false } = {}) => {
  try {
    const { data } = await client.post(`/api/v1/ai-image-masking/import-jobs/${jobId}/cancel`, null, {
      params: { cleanup },
    });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const testReportQuery = async ({ endpoint, index, apiKey, query, authType, username, password }) => {
  try {
    const { data } = await client.post('/api/v1/reports/test-query', {
      endpoint,
      index,
      api_key: apiKey,
      auth_type: authType,
      username,
      password,
      query,
    });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

